import * as THREE from 'three';

/**
 * Weapon inspection controller.
 * When activated, takes control of the weapon viewmodel group,
 * centers it on screen, and allows orbit rotation via mouse.
 */
export class WeaponInspector {
  private inspecting = false;
  private weaponGroup: THREE.Group | null = null;

  // Orbit state
  private yaw = 0;
  private pitch = 0;
  private zoom = 1;

  // Saved viewmodel state for restoration
  private savedPosition = new THREE.Vector3();
  private savedRotation = new THREE.Euler();

  // Smooth animation
  private transitionProgress = 0;
  private transitionSpeed = 4; // units per second

  // Inspect position (centered, slightly forward)
  private readonly inspectOffset = new THREE.Vector3(0.0, -0.08, -0.5);

  // F-CM-006: document-level mouse/wheel handlers attached only while
  // inspecting so the orbit + zoom state is actually driven by user input.
  // We listen on document so the existing pointer-lock movementX/Y stream
  // (used by camera.ts for aim) reaches the inspector even though the camera
  // continues consuming the same events for its own yaw/pitch.
  private readonly _onDocumentMouseMove: (e: MouseEvent) => void;
  private readonly _onDocumentWheel: (e: WheelEvent) => void;
  private listenersAttached = false;

  constructor() {
    this._onDocumentMouseMove = (e: MouseEvent) => {
      if (!this.inspecting) return;
      // movementX/Y are pointer-lock deltas in pixels. Match the hand-tuned
      // sensitivities baked into onMouseMove/onScroll.
      this.onMouseMove(e.movementX, e.movementY);
    };
    this._onDocumentWheel = (e: WheelEvent) => {
      if (!this.inspecting) return;
      this.onScroll(e.deltaY);
    };
  }

  get isInspecting(): boolean { return this.inspecting; }

  /**
   * Start inspecting the currently held weapon.
   * Saves current position/rotation for later restoration.
   */
  startInspect(weaponGroup: THREE.Group): void {
    if (this.inspecting) return;
    this.inspecting = true;
    this.weaponGroup = weaponGroup;
    this.yaw = 0;
    this.pitch = 0;
    this.zoom = 1;
    this.transitionProgress = 0;

    // Save current viewmodel state
    this.savedPosition.copy(weaponGroup.position);
    this.savedRotation.copy(weaponGroup.rotation);

    // F-CM-006: subscribe to mouse/wheel input only while inspecting so the
    // orbit + zoom state actually advances. Skip in non-DOM environments
    // (vitest unit tests construct the inspector without a document).
    if (!this.listenersAttached && typeof document !== 'undefined') {
      document.addEventListener('mousemove', this._onDocumentMouseMove);
      document.addEventListener('wheel', this._onDocumentWheel, { passive: true });
      this.listenersAttached = true;
    }
  }

  /**
   * Stop inspection and return weapon to normal viewmodel position.
   */
  stopInspect(): void {
    if (!this.inspecting || !this.weaponGroup) return;

    // Restore saved position/rotation
    this.weaponGroup.position.copy(this.savedPosition);
    this.weaponGroup.rotation.copy(this.savedRotation);

    this.inspecting = false;
    this.weaponGroup = null;

    // F-CM-006: drop input listeners when leaving inspect so the inspector
    // doesn't keep consuming wheel events outside its mode.
    if (this.listenersAttached && typeof document !== 'undefined') {
      document.removeEventListener('mousemove', this._onDocumentMouseMove);
      document.removeEventListener('wheel', this._onDocumentWheel);
      this.listenersAttached = false;
    }
  }

  /**
   * Toggle inspection on/off.
   */
  toggleInspect(weaponGroup: THREE.Group): void {
    if (this.inspecting) {
      this.stopInspect();
    } else {
      this.startInspect(weaponGroup);
    }
  }

  /**
   * Handle mouse movement during inspection (orbit).
   * @param deltaX - Mouse X delta (pixels)
   * @param deltaY - Mouse Y delta (pixels)
   */
  onMouseMove(deltaX: number, deltaY: number): void {
    if (!this.inspecting) return;
    this.yaw += deltaX * 0.005;
    this.pitch += deltaY * 0.005;
    // Clamp pitch to prevent flipping
    this.pitch = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, this.pitch));
  }

  /**
   * Handle scroll wheel for zoom during inspection.
   * @param delta - Scroll delta (positive = zoom in)
   */
  onScroll(delta: number): void {
    if (!this.inspecting) return;
    this.zoom = Math.max(0.5, Math.min(2.0, this.zoom - delta * 0.001));
  }

  /**
   * Update inspection animation. Call every frame.
   * @param dt - Delta time in seconds
   */
  update(dt: number): void {
    if (!this.inspecting || !this.weaponGroup) return;

    // Smooth transition to inspect position
    this.transitionProgress = Math.min(1, this.transitionProgress + dt * this.transitionSpeed);
    const t = this.smoothstep(this.transitionProgress);

    // Interpolate position from saved to inspect offset
    const targetX = this.inspectOffset.x;
    const targetY = this.inspectOffset.y;
    const targetZ = this.inspectOffset.z * this.zoom;

    this.weaponGroup.position.set(
      this.savedPosition.x + (targetX - this.savedPosition.x) * t,
      this.savedPosition.y + (targetY - this.savedPosition.y) * t,
      this.savedPosition.z + (targetZ - this.savedPosition.z) * t,
    );

    // Apply orbit rotation (blended with transition)
    this.weaponGroup.rotation.set(
      this.pitch * t,
      this.yaw * t + Math.PI,  // +PI so weapon faces the camera
      0,
    );
  }

  /** Reset orbit state without stopping inspection. */
  resetOrbit(): void {
    this.yaw = 0;
    this.pitch = 0;
    this.zoom = 1;
  }

  private smoothstep(t: number): number {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
  }
}
